import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';

interface CalendarWidgetProps {
  selectedWeekStart: string; // YYYY-MM-DD (Monday)
  onWeekChange?: (weekStart: string) => void;
}

export const CalendarWidget: React.FC<CalendarWidgetProps> = ({
  selectedWeekStart,
  onWeekChange,
}) => {
  const today = new Date();
  const viewYearFromWeek = useMemo(() => {
    const ys = selectedWeekStart.slice(0, 4);
    const ms = selectedWeekStart.slice(5, 7);
    return parseInt(ys, 10) * 12 + parseInt(ms, 10) - 1;
  }, [selectedWeekStart]);

  const [viewYear, setViewYear] = useState(viewYearFromWeek);
  useEffect(() => {
    setViewYear(viewYearFromWeek);
  }, [viewYearFromWeek]);

  const viewMonth = viewYear % 12;
  const viewYearOnly = Math.floor(viewYear / 12);

  const firstDayOfMonth = new Date(viewYearOnly, viewMonth, 1);
  const lastDayOfMonth = new Date(viewYearOnly, viewMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const adjustedStartingDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

  const days: (number | null)[] = [];
  for (let i = 0; i < adjustedStartingDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const selectedStart = useMemo(() => new Date(selectedWeekStart + 'T12:00:00'), [selectedWeekStart]);
  const selectedEnd = useMemo(() => {
    const d = new Date(selectedStart);
    d.setDate(d.getDate() + 6);
    return d;
  }, [selectedStart]);

  const isInSelectedWeek = (day: number | null): boolean => {
    if (day === null) return false;
    const cellDate = new Date(viewYearOnly, viewMonth, day);
    return cellDate >= selectedStart && cellDate <= selectedEnd;
  };

  const getWeekStartForDay = (day: number): string => {
    const d = new Date(viewYearOnly, viewMonth, day);
    const dayOfWeek = d.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    d.setDate(d.getDate() + mondayOffset);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dayStr = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dayStr}`;
  };

  const isToday = (day: number | null): boolean => {
    if (day === null) return false;
    return (
      viewYearOnly === today.getFullYear() &&
      viewMonth === today.getMonth() &&
      day === today.getDate()
    );
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const goPrevMonth = () => setViewYear((v) => v - 1);
  const goNextMonth = () => setViewYear((v) => v + 1);

  const weeks: (number | null)[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <TouchableOpacity onPress={goPrevMonth} style={styles.navButton}>
          <Text style={styles.navButtonText}>←</Text>
        </TouchableOpacity>
        <Text style={styles.monthTitle}>
          {monthNames[viewMonth]} {viewYearOnly}
        </Text>
        <TouchableOpacity onPress={goNextMonth} style={styles.navButton}>
          <Text style={styles.navButtonText}>→</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.weekDaysRow}>
        {weekDays.map((day, index) => (
          <View key={index} style={styles.weekDayCell}>
            <Text style={styles.weekDayText}>{day}</Text>
          </View>
        ))}
      </View>

      <View style={styles.daysGrid}>
        {weeks.map((week, weekIndex) => {
          const weekSelected = week.some((d) => d !== null && isInSelectedWeek(d));
          return (
            <View key={weekIndex} style={styles.weekRow}>
              {weekSelected && <View style={styles.weekHighlight} />}
              {week.map((day, dayIndex) => {
                if (day === null) {
                  return <View key={dayIndex} style={styles.dayCell} />;
                }
                const inSelectedWeek = isInSelectedWeek(day);
                const isCurrentDay = isToday(day);
                return (
                  <TouchableOpacity
                    key={dayIndex}
                    style={[styles.dayCell, isCurrentDay && styles.todayCell]}
                    onPress={() => {
                      const weekStart = getWeekStartForDay(day);
                      onWeekChange?.(weekStart);
                    }}
                  >
                    <Text
                      style={[
                        styles.dayText,
                        inSelectedWeek && styles.selectedDayText,
                        isCurrentDay && styles.todayText,
                      ]}
                    >
                      {day}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
    width: 320,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  navButton: {
    padding: 8,
    minWidth: 36,
    alignItems: 'center',
  },
  navButtonText: {
    fontSize: 18,
    color: '#0891b2',
    fontFamily: 'Montserrat_600SemiBold',
  },
  monthTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Montserrat_600SemiBold',
  },
  weekDaysRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  weekDayCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  weekDayText: {
    fontSize: 13,
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
  },
  daysGrid: {
    flexDirection: 'column',
  },
  weekRow: {
    flexDirection: 'row',
    position: 'relative',
    marginBottom: 4,
  },
  weekHighlight: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#E0F2FE',
    borderRadius: 8,
    zIndex: -1,
  },
  dayCell: {
    width: '14.28%',
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 2,
  },
  todayCell: {
    backgroundColor: '#0891B2',
    borderRadius: 8,
    padding: 4,
  },
  dayText: {
    fontSize: 14,
    color: '#1F2937',
    fontFamily: 'Montserrat_500Medium',
  },
  selectedDayText: {
    color: '#0891B2',
    fontFamily: 'Montserrat_600SemiBold',
  },
  todayText: {
    color: '#FFFFFF',
    fontFamily: 'Montserrat_600SemiBold',
  },
});
